import { Link } from "react-router-dom";
import { MapPin, Mail, Clock, ShieldCheck, Phone } from "lucide-react";
import Logo from "@/components/Logo";

const linkCols = [
  {
    title: "Company",
    links: [
      { to: "/", label: "Home" },
      { to: "/about", label: "About" },
      { to: "/sustainability", label: "Sustainability" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    title: "Products",
    links: [
      { to: "/products", label: "All Categories" },
      { to: "/condition-guide", label: "Condition Guide" },
      { to: "/process", label: "Our Process" },
    ],
  },
  {
    title: "Customer Care",
    links: [
      { to: "/returns", label: "Returns & Support" },
      { to: "/contact", label: "Contact Support" },
      { to: "/legal", label: "Terms of Service" },
      { to: "/privacy", label: "Privacy Policy" },
    ],
  },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="bg-[#0B192C] text-white"
      data-testid="site-footer"
    >
      <div className="cnl-container py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand block */}
          <div className="md:col-span-5">
            <Logo variant="inverse" />
            <p className="mt-6 text-[14px] leading-relaxed text-gray-300 max-w-md">
              CNL Tech is an independently owned electronics reseller in
              Corona, California. We source, inspect, and resell open-box,
              refurbished, customer-return, and overstock consumer electronics —
              giving quality hardware a second life since 2020.
            </p>
            <div className="mt-6 space-y-3 text-[13.5px] text-gray-300">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-gray-400 shrink-0" />
                <span>
                  11762 De Palma Rd Ste 1C
                  <br />
                  Corona, CA 92883-4010, USA
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-gray-400 shrink-0" />
                <span>Monday – Friday · 9:00 AM – 5:00 PM PT</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gray-400 shrink-0" />
                <a
                  href="mailto:cnltech@proton.me"
                  className="hover:text-white transition-colors"
                  data-testid="footer-email-link"
                >
                  cnltech@proton.me
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-gray-400 shrink-0" />
                <a
                  href="tel:+19514792470"
                  className="hover:text-white transition-colors"
                  data-testid="footer-phone-link"
                >
                  +1 (951) 479-2470
                </a>
              </div>
            </div>

            <div className="mt-6 inline-flex items-center gap-2 rounded-sm border border-[#1E3E62] px-3 py-1.5">
              <ShieldCheck size={14} className="text-[#16A34A]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-200">
                Serving Customers Since 2020
              </span>
            </div>
          </div>

          {/* Link columns */}
          {linkCols.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <h4
                className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400 mb-5"
                style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
              >
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-[14px] text-gray-300 hover:text-white transition-colors"
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Tagline strip */}
          <div className="md:col-span-1 hidden md:flex flex-col items-end">
            <div className="text-right text-[11px] uppercase tracking-[0.22em] text-gray-500 leading-relaxed">
              Open Box
              <br />
              Refurbished
              <br />
              Overstock
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#1E3E62]">
        <div className="cnl-container py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-[12px] text-gray-400">
            © 2020 CNL Tech · Independent Electronics Reseller · Owner: Marcelo Peñaloza
          </p>
          <p className="text-[12px] text-gray-500 tracking-wide">
            All trademarks, logos and product names are the property of their respective owners.
          </p>
        </div>
      </div>
    </footer>
  );
}
